import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { LocationState } from 'redux-first-router';
import { shallowEqual, useSelector } from 'react-redux';

import { useSummaryV2 } from '@/hooks/useCompany';
import { useCompanyLimits } from '@/hooks/useCompanyLimits';
import { useCompanyShareLink } from '@/hooks/useCompanyShareToken';
import type { IntersectionPayload } from '@/hooks/useIntersectionListener';

import {
  ROUTE_COMPANY,
  ROUTE_COMPANY_WITH_EVENTS,
  ROUTE_COMPARE_COMPANY,
  ROUTE_COMPARE_HOME,
  ROUTE_LOCALE_COMPANY,
  ROUTE_LOCALE_COMPANY_WITH_EVENTS,
} from '@/constants/routes';

import type {
  CompanySubSection,
  UpdateMethod,
} from '../../hooks/useReportUrlHandler';
import useReportUrlHandler from '../../hooks/useReportUrlHandler';

interface ContextValue {
  showRegistration: boolean;
  showPlansModal: boolean;
  onIntersectionPathUpdate: (
    data: IntersectionPayload,
    shareToken: string,
    section?: string | undefined
  ) => void;
  goToSection: (section: CompanySubSection, method?: UpdateMethod) => void;
  onNavigation: (section: CompanySubSection, method?: UpdateMethod) => void;
  setShowRegistration: (show: boolean) => void;
  setShowPlansModal: (show: boolean) => void;
}

const ReportSplitHandlerContext = createContext<ContextValue | undefined>(
  undefined
);

export function useReportSplitHandler() {
  const context = useContext(ReportSplitHandlerContext);
  const validUse = useSelector<{ location: LocationState }, boolean>(
    (state) =>
      [
        ROUTE_COMPANY,
        ROUTE_LOCALE_COMPANY,
        ROUTE_COMPANY_WITH_EVENTS,
        ROUTE_LOCALE_COMPANY_WITH_EVENTS,
        ROUTE_COMPARE_COMPANY,
        ROUTE_COMPARE_HOME,
      ].includes(state.location.type),
    shallowEqual
  );
  if (!validUse) {
    console.warn(
      'useReportSplitHandler should only be used in company report or compare pages'
    );
  }

  //TODO: @victorc make this work for compare pages
  if (context === undefined) {
    return {
      showFullReport: false,
      showRegistration: false,
      showPlansModal: false,
      onIntersectionPathUpdate: () => undefined,
      goToSection: () => undefined,
      onNavigation: () => undefined,
      setShowRegistration: () => undefined,
      setShowPlansModal: () => undefined,
    };
  }
  return context;
}

interface Props {
  children: React.ReactElement;
}
const ReportSplitHandlerProvider = (props: Props) => {
  const { onIntersectionPathUpdate, goToSection } = useReportUrlHandler();

  const [showRegistration, setShowRegistration] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const summary = useSummaryV2();
  const companyId =
    summary.status === 'success' ? summary.companyId : undefined;
  const limits = useCompanyLimits(companyId);

  const hasCredits = !limits.restricted || limits.visited;
  const { isSharedCompany } = useCompanyShareLink();

  const onNavigation = useCallback(
    (section: CompanySubSection, method: UpdateMethod = 'push') => {
      // Avoid showing the plans modal when using the nav links
      if (section === 'summary') {
        setShowPlansModal(false);
        return;
      }
      if (!hasCredits && !isSharedCompany) {
        setShowPlansModal(true);
        return;
      }
      goToSection(section, method);
    },
    [hasCredits, goToSection, isSharedCompany, setShowPlansModal]
  );

  const value = useMemo(
    () => ({
      showRegistration,
      showPlansModal,
      onIntersectionPathUpdate,
      goToSection,
      onNavigation,
      setShowRegistration,
      setShowPlansModal,
    }),
    [
      showRegistration,
      showPlansModal,
      onIntersectionPathUpdate,
      goToSection,
      onNavigation,
      setShowRegistration,
      setShowPlansModal,
    ]
  );

  return (
    <ReportSplitHandlerContext.Provider value={value}>
      {props.children}
    </ReportSplitHandlerContext.Provider>
  );
};

export default ReportSplitHandlerProvider;
